<template>
  <div
    v-if="authStore.hasFeature('enterprise')"
    class="field has-addons"
  >
    <p class="control is-expanded">
      <span
        class="input"
        :class="classes"
        readonly
        :disabled="!fieldDefinition.editable || undefined"
        v-on:click="openModal"
        >{{ fieldData.value ?? '' }}</span
      >
    </p>
    <p
      class="control"
      v-if="fieldDefinition.editable"
    >
      <button
        title="Select a secret"
        class="button has-text-primary"
        type="button"
        v-on:click="openModal"
        :disabled="fieldData.loading || undefined"
      >
        <i class="icon-edit"></i>
      </button>
    </p>
    <p
      class="control"
      v-if="fieldDefinition.editable && fieldData.value !== null && !fieldDefinition.required"
    >
      <button
        class="button has-text-danger"
        type="button"
        v-on:click="removeSecret"
        title="Deselect secret"
        :disabled="fieldData.loading || undefined"
      >
        <i class="icon-minus"></i>
      </button>
    </p>
  </div>
  <!-- Fallback for Community Edition: the field behaves like a `text` field -->
  <textarea
    v-else
    class="textarea is-family-monospace"
    v-model.trim="fieldData.value"
    :class="classes"
    placeholder="Secret text"
    :disabled="fieldData.loading || !fieldDefinition.editable || undefined"
  ></textarea>
  <p
    class="help is-danger"
    v-for="(error, i) in [...localErrors, ...fieldData.errors]"
    :key="i"
  >
    {{ error }}
  </p>
  <Modal
    v-if="selectionModal"
    v-model="selectionModal"
    title="Select a secret"
    is-large
  >
    <SecretList
      selectable
      v-on:select="selectSecret"
    />
    <!-- Leave the footer empty -->
    <template v-slot:footer></template>
  </Modal>
</template>

<script setup lang="ts">
import { isAxiosError } from 'axios'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import Modal from '@/components/Modal.vue'
import { errorParser } from '@/helpers'
import { useAuthStore, useSecretStore } from '@/stores'
import type { SecretLight } from '@/types/secret'
import SecretList from '@/views/Secret/List.vue'

import { type FieldModel, type FieldProps, ValidationError, defineValidator } from '.'

type Constraint = { type: 'secret'; many: false }
const { fieldDefinition } = defineProps<FieldProps<Constraint>>()
const fieldData = defineModel<FieldModel<Constraint>>({ required: true })

const classes = computed(() => ({
  'is-danger': !fieldData.value.valid,
  'is-loading': fieldData.value.loading,
  'is-clickable': fieldDefinition.editable || !!fieldData.value.value,
}))

const selectionModal = ref(false)

const router = useRouter()
const authStore = useAuthStore()
const secretStore = useSecretStore()

async function validator(name: unknown) {
  // Do not perform any validation in Community Edition, this is a text field
  if (!authStore.hasFeature('enterprise')) return

  if (name === null) return
  if (typeof name !== 'string') throw new ValidationError(['Secret name should be a string'])
  // We already know about this secret, it is valid
  if (name in secretStore.secrets) return

  try {
    await secretStore.get(name)
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404)
      throw new ValidationError(['This secret does not exist or you do not have access to it'])
    throw new ValidationError([`Could not retrieve this secret: ${errorParser(err)}`])
  }
}
const { localErrors } = defineValidator(fieldDefinition, fieldData.value, validator)

function openModal() {
  if (fieldData.value.loading) return
  // On an editable field, open the picker modal
  if (fieldDefinition.editable) selectionModal.value = true
  // On a non-editable field, open the model details in a new tab when a model is set
  else if (!fieldData.value.value) return
  else
    window.open(
      router.resolve({ name: 'secret-details', params: { name: fieldData.value.value } }).href,
      '_blank',
    )
}

function selectSecret(secret: SecretLight) {
  fieldData.value.value = secret.name
  // Close the modal immediately after selecting a version
  selectionModal.value = false
}

function removeSecret() {
  if (!fieldData.value.loading) fieldData.value.value = null
}
</script>

<style lang="css" scoped>
.field + .help {
  margin-top: calc(-1 * var(--bulma-block-spacing));
}
</style>
